import { useEffect } from 'react';
import { useDataProvider } from '@/data/provider';
import { readSupabaseEnvironment } from '@/data/supabase/config';
import {
  checkRemoteFreshness,
  clearDisabledTableEntries,
  getPendingSyncCount,
  pullUpdates,
  pushOutbox,
  resetStaleRetries,
} from '@/data/sync';
import { refreshWorkingSet } from '@/data/offline/workingSetService';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useDevNetworkOverride } from '@/data/devNetworkOverride';

const PUSH_INTERVAL_MS = 5000;
const FRESHNESS_CHECK_INTERVAL_MS = 30 * 1000; // 30 seconds - lightweight check for remote changes
const FULL_PULL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes - full pull as backup
const WORKING_SET_REFRESH_MS = 60 * 1000;
const STALE_RETRY_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
const STALE_RETRY_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour
const MAX_AUTO_RETRIES = 5;

export function SyncScheduler(): null {
  const { mode } = useDataProvider();
  const { isOnline } = useNetworkStatus();
  const { forceOffline } = useDevNetworkOverride();
  const supabaseEnv = readSupabaseEnvironment();
  const syncEnabled = mode === 'synced' && supabaseEnv.isConfigured && supabaseEnv.syncEnabled;
  const workingSetRefreshEnabled = mode !== 'local' && !forceOffline;

  useEffect(() => {
    if (!syncEnabled || !isOnline) return;

    let cancelled = false;

    const runPush = async () => {
      if (cancelled) return;
      const pending = await getPendingSyncCount();
      if (pending > 0) {
        await pushOutbox();
      }
    };

    const runPull = async () => {
      if (cancelled) return;
      const result = await pullUpdates();
      // Trigger working set refresh if new data was applied
      if (result.applied > 0) {
        await refreshWorkingSet();
      }
    };

    // Lightweight freshness check - only pull if remote has newer data
    const runFreshnessCheck = async () => {
      if (cancelled) return;
      const hasChanges = await checkRemoteFreshness();
      if (hasChanges) {
        const result = await pullUpdates();
        // Trigger working set refresh if new data was applied
        if (result.applied > 0) {
          await refreshWorkingSet();
        }
      }
    };

    const runStaleRetryReset = async () => {
      if (cancelled) return;
      await resetStaleRetries(MAX_AUTO_RETRIES, STALE_RETRY_THRESHOLD_MS);
    };

    // Clean up stale entries for tables that no longer sync
    void clearDisabledTableEntries();
    void runPull();
    void runPush();

    const pushInterval = window.setInterval(runPush, PUSH_INTERVAL_MS);
    // Use frequent freshness checks (30s) to detect changes quickly
    const freshnessCheckInterval = window.setInterval(
      runFreshnessCheck,
      FRESHNESS_CHECK_INTERVAL_MS
    );
    // Keep full pull as backup (5 min) in case freshness check misses something
    const fullPullInterval = window.setInterval(runPull, FULL_PULL_INTERVAL_MS);
    const staleRetryInterval = window.setInterval(runStaleRetryReset, STALE_RETRY_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(pushInterval);
      window.clearInterval(freshnessCheckInterval);
      window.clearInterval(fullPullInterval);
      window.clearInterval(staleRetryInterval);
    };
  }, [isOnline, syncEnabled]);

  useEffect(() => {
    if (!workingSetRefreshEnabled) return;

    let cancelled = false;

    const runRefresh = async () => {
      if (cancelled) return;
      await refreshWorkingSet();
    };

    void runRefresh();

    const refreshInterval = window.setInterval(runRefresh, WORKING_SET_REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(refreshInterval);
    };
  }, [workingSetRefreshEnabled]);

  return null;
}
